import { LngLatBounds, Map } from 'mapbox-gl';

import { MapStyleSelectType } from './constants';
import { LatLng, LngLat, LngLatObj } from './types';

/**
 * Our Mapbox access token is globally available at `process.env.MAPBOX_ACCESS_TOKEN` but
 * we do allow for a custom access token to be passed in to a `Map` or `Geocoder`
 * component if the consuming application has its own token.
 *
 * @param {string | undefined} token Mapbox API access token
 * @returns {string | null} access token
 */
export const getAccessToken = (token: string | undefined) => {
  if (token) {
    return token;
  }

  // try catch here prevents "ReferenceError: process is not defined" which can happen
  // when webpack or tooling that sets process.env values is not configured correctly or
  // if MAPBOX_ACCESS_TOKEN is missing
  try {
    return process?.env?.MAPBOX_ACCESS_TOKEN ?? null;
  } catch {
    // ignore as we want to suppress the error
  }

  return null;
};

/**
 * Map Strava specific local to Mapbox expected local
 * @param {string} lang Strava locale string
 * @returns {string} MapboxLocal expected locale
 */
export const languageMapper = (lang: string) => {
  const regex = new RegExp('(.*)-');
  const shortLang = regex.exec(lang);

  switch (lang) {
    case 'zh':
    case 'zh-CN':
      return 'zh-Hans';
    case 'zh-TW':
      return 'zh-Hant-TW';
    default:
      if (shortLang) {
        return shortLang[1];
      }
      return lang;
  }
};

/**
 * Return a Set of className from a string
 * @param {string | null | undefined} className
 * @returns
 */
export const getClassNameSet = (className: string | null | undefined) => {
  return new Set(className ? className.trim().split(/\s+/) : []);
};

/**
 * Convert single [lat, lng] or list of [lat, lng] to [lng, lat]
 * @param {array} latLng single [lat, lng] or list containing [lat, lng]
 */
export const latLngToLngLat = (latLng: LatLng | LatLng[]) => {
  // TODO once all code that imports this method has been updated to TS, we can remove
  // this check.
  if (!latLng || latLng.length === 0) {
    return [];
  }

  if (Array.isArray(latLng[0])) {
    return (latLng as LatLng[]).map((p) => [p[1], p[0]]);
  }

  return [latLng[1], latLng[0]];
};

/**
 * Get if a given point is in the bounds of the map
 * @param {LngLatObj} point Object containing lat and lng coordinates
 * @param {LngLatBounds} bounds map.getBounds() object
 */
export const inBounds = (point: LngLatObj, bounds: LngLatBounds) => {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  const lg = (point.lng - northEast.lng) * (point.lng - southWest.lng) < 0;
  const lt = (point.lat - northEast.lat) * (point.lat - southWest.lat) < 0;

  return lg && lt;
};

/**
 * Returns coordinates for polygon circle
 *
 * @param {LatLng} center Array containing [lat, lng]
 * @param {number} radius Radius size in kilometers
 * @param {number} points How many points to use to generate a circle
 *
 * @returns {array} Array of [lng, lat] coordinates for a circle
 */
export const getCircleCoords = (
  center: LatLng,
  radius: number,
  points = 32
): LngLat[] => {
  const centerCoords: LngLatObj = {
    lng: center[0],
    lat: center[1]
  };
  const distanceX =
    radius / (111.32 * Math.cos((centerCoords.lat * Math.PI) / 180));
  const distanceY = radius / 110.574;

  const coords: LngLat[] = [];

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);

    coords.push([centerCoords.lng + x, centerCoords.lat + y]);
  }
  // Close the circle
  if (coords[0]) {
    coords.push(coords[0]);
  }

  return coords;
};

/**
 * Returns id if layer exists on the map and null when it does not, this prevents
 * mapbox-gl throwing an error when layer does not exist and not drawing anything on the
 * map
 * @param {MapboxInstance} map mapbox-gl instance
 * @param {string} id Layer id
 *
 * @returns  id when layer exists and null if layer does not exist
 */
export const getBeforeIdToUse = (map: Map, id: string) =>
  map.getLayer(id) ? id : undefined;

/**
 * When switching route types in the route builder UI, some layers (trail networks, POIs) will not
 * update automatically unless the user interacts with the map (changes zoom level, clicks and
 * drags, etc). By clearing the tiles associated with those layers and reloading the layers the map
 * will update without any user interaction. This also allows us to append any query params we may
 * want to exist on tile requests.
 * @param map mapbox instance
 * @param {string} source Source id
 * @param {string} params Params that will be added to the tile URL at the end
 */
export const reloadLayerSource = (map: Map, source: string, params: string) => {
  let mapSource = map.getSource(source);

  // If for some reason the requested source isn't available, return early before reloading to avoid
  // breaking the map
  if (!mapSource) {
    return;
  }

  const onStyleData = () => {
    mapSource = map.getSource(source);
    // @ts-expect-error Accessing private  variable that is not exposed in types
    const { tiles } = mapSource;

    // When map style changes happen tiles can be undefined so we wait until tile are ready
    if (tiles) {
      map.off('styledata', onStyleData);

      // Get the base tile URL and append the given query params
      //
      // TODO: Potentially look into using URLSearchParams here so that when tile URL has
      // other params we don't conflict or override them unintentionally
      const updatedTiles = tiles.map((tile: string) => {
        const [baseUrl] = tile.split('?');
        return `${baseUrl}${params}`;
      });

      // Ideally we would want to use `setTiles` here as it is the native API to update
      // tiles, however on local development Nextjs's Fast Refresh does not work properly
      // and throws an error, we currently do not know the detail why and it might be
      // resolved in more modern Nextjs versions.
      //
      // So for now we are using internal API to update the tiles and force the map to
      // reload the tiles.
      //
      // mapSource.setTiles(updatedTiles);

      // Update the tiles URL
      // @ts-expect-error Accessing private  variable that is not exposed in types
      map.getSource(source).tiles = updatedTiles;

      // Clear and update tile caches
      // https://github.com/mapbox/mapbox-gl-js/issues/2941
      // @ts-expect-error Accessing private  variable that is not exposed in types
      map.style._sourceCaches[`other:${source}`].clearTiles();
      // @ts-expect-error Accessing private  variable that is not exposed in types
      map.style._sourceCaches[`other:${source}`].update(map.transform);
      // @ts-expect-error Accessing private  variable that is not exposed in types
      map.style._sourceCaches[`symbol:${source}`].clearTiles();
      // @ts-expect-error Accessing private  variable that is not exposed in types
      map.style._sourceCaches[`symbol:${source}`].update(map.transform);
    }
  };

  // When map style changes happen tiles can be undefined so we wait until tiles exist,
  // however when tiles are already available we can immediately update them.
  //
  // @ts-expect-error Accessing private  variable that is not exposed in types
  if (mapSource?.tiles) {
    onStyleData();
  } else {
    map.on('styledata', onStyleData);
  }
};

/**
 * GraphQL returns route type as a string, and route_type param that needs to be sent to
 * /tiles/pois endpoint needs to be a thrift number
 * @param {string} routeType
 * @returns {number}
 */
export const getThriftRouteTypeFromGraphQlRouteType = (routeType: string) => {
  switch (routeType) {
    case 'Ride':
      return 1;
    case 'Run':
      return 2;
    case 'Walk':
      return 3;
    case 'Hike':
      return 4;
    case 'TrailRun':
      return 5;
    case 'GravelRide':
      return 6;
    case 'MountainBikeRide':
      return 7;
    case 'Swim':
      return 10;
    default:
      // eslint-disable-next-line no-console
      console.warn(
        `Route type "${routeType}" is not supported as POI on the map`
      );
      return null;
  }
};

export const isLightMapStyle = (mapStyleKey: MapStyleSelectType) =>
  ['standard', 'winter', 'light'].includes(mapStyleKey);
